import React from 'react';

import { Box, Button, ButtonGroup, InputAdornment, OutlinedInput } from '@mui/material';

import { debounce, uniq } from 'lodash';

import { CheckboxMultiSelect, CheckboxOption } from '../../components/CheckboxMultiSelect/CheckboxMultiSelect';
import { ProductRequestsDialog } from '../../components/Dialogs/ProductRequestsDialog/ProductRequestsDialog';
import { ProductRequestsFiltersDialog } from '../../components/Dialogs/ProductRequestsFiltersDialog/ProductRequestsFiltersDialog';
import { OfferStatus, ShippingRegion, VenueListAccessRequetsFilter } from '../../graphql/API';
import useAccessRequestQuery from '../../hooks/reactQuery/useAccessRequestQuery';
import { useListOfferQuery } from '../../hooks/reactQuery/useOfferQuery';
import Icons from '../../themes/icons';
import { toolbarControlSx } from '../../themes/toolbar';
import { AccessRequestPageView } from './AccessRequest';

type Props = {
  filters?: Partial<VenueListAccessRequetsFilter>;
  setFilters: React.Dispatch<
    React.SetStateAction<Partial<VenueListAccessRequetsFilter> | undefined>
  >;
  view: AccessRequestPageView;
  handleView: (val: AccessRequestPageView) => void;
  selectedOfferIds?: number[];
  onSelectedOfferIdsChange?: (ids: number[]) => void;
};

export const ProductRequestsHeader: React.FC<Props> = ({
  filters,
  setFilters,
  view,
  handleView,
  selectedOfferIds = [],
  onSelectedOfferIdsChange,
}) => {
  const [productRequestsHistoryDialog, setProductRequestsHistoryDialog] = React.useState(false);
  const [productRequestsFiltersDialog, setProductRequestsFiltersDialog] = React.useState(false);
  const [selectedOffers, setSelectedOffers] = React.useState<CheckboxOption[]>([]);

  const { data: allAccessRequests } = useAccessRequestQuery(
    {
      status: filters?.status,
    },
    false,
    1000,
  );

  const offerIdsWithRequests = React.useMemo(() => {
    if (!allAccessRequests) return [];
    const ids = allAccessRequests
      .map((req) => req.offerID)
      .filter((id): id is number => id !== null && id !== undefined);
    return uniq(ids);
  }, [allAccessRequests]);

  const { data: allOffers } = useListOfferQuery({
    status: OfferStatus.active,
  });

  const availableOffers = React.useMemo(() => {
    if (!allOffers || !offerIdsWithRequests.length) return [];
    return allOffers.filter(
      (offer) => offerIdsWithRequests.includes(Number(offer.id)) && offer.status === OfferStatus.active,
    );
  }, [allOffers, offerIdsWithRequests]);

  const offerOptions: CheckboxOption[] = React.useMemo(() => {
    const offerOptionsList: CheckboxOption[] = availableOffers.map((offer) => ({
      label: offer.title || '',
      value: Number(offer.id),
      hideCheckbox: false,
    }));

    return offerOptionsList.sort((a, b) => a.label.localeCompare(b.label));
  }, [availableOffers]);

  const handleProductRequestsHistoryDialog = () => {
    setProductRequestsHistoryDialog((prev) => !prev);
  };

  const handleProductRequestsFiltersDialog = () => {
    setProductRequestsFiltersDialog((prev) => !prev);
  };

  const onHandleFilterChange = (filters: Partial<VenueListAccessRequetsFilter> | undefined) => {
    setFilters(filters);
  };

  const handleSearch = debounce((value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      searchText: value,
    }));
  }, 500);

  const handleOffersChange = (value: CheckboxOption | CheckboxOption[] | null) => {
    const selected = Array.isArray(value) ? value : value ? [value] : [];
    setSelectedOffers(selected)
    setFilters((prevFilters) => ({
      ...prevFilters,
      offerIDList: selected.map(item => Number(item.value)),
    }));
  };

  const isAnyFilterActive = React.useCallback(
    (filters: Partial<VenueListAccessRequetsFilter> | undefined) => {
      return (
        filters &&
        ((filters['minFollowers'] && filters['minFollowers'] > 0) ||
          ((filters['marketRegions'] && filters['marketRegions']?.length) || 0) > 0 ||
          filters['rating'] !== undefined ||
          filters['instagramVerified'] !== undefined)
      );
    },
    [],
  );

  return (
    <>
      <ButtonGroup variant='outlined' size='medium' sx={toolbarControlSx}>
        <Button
          sx={{
            ...toolbarControlSx,
            border: '1px solid #EFEEEE',
            ...(view === 'grid' && { color: '#765BC6', backgroundColor: '#F1EFF9' }),
          }}
          onClick={() => handleView('grid')}
        >
          <Icons.GridIconV2 />
        </Button>
        <Button
          sx={{
            ...toolbarControlSx,
            border: '1px solid #EFEEEE',
            ...(view === 'list' && { color: '#765BC6', backgroundColor: '#F1EFF9' }),
          }}
          onClick={() => handleView('list')}
        >
          <Icons.ListIconV2 />
        </Button>
      </ButtonGroup>

      <Button
        id='btn-filter'
        startIcon={
          <Icons.TuneOutlinedIcon htmlColor={isAnyFilterActive(filters) ? '#765BC6' : ''} />
        }
        onClick={handleProductRequestsFiltersDialog}
        sx={{
          ...toolbarControlSx,
          border: '1px solid #EFEEEE',
          px: 1.5,
          // MUI pulls startIcon 4px into the left padding by default, which
          // left the icon visibly tighter to the border than the label is on
          // the right. Reset it so both sides are a true 12px.
          '& .MuiButton-startIcon': { ml: 0 },
        }}
      >
        <span style={{ color: isAnyFilterActive(filters) ? '#765BC6' : '' }}>Filters</span>
      </Button>

      <Button
        id='btn-history'
        startIcon={<Icons.HistoryIcon />}
        onClick={handleProductRequestsHistoryDialog}
        sx={{
          ...toolbarControlSx,
          border: '1px solid #EFEEEE',
          px: 1.5,
          '& .MuiButton-startIcon': { ml: 0 },
        }}
      >
        History
      </Button>

      {/* CheckboxMultiSelect's control is already TOOLBAR_CONTROL_HEIGHT tall —
          it is the reference the other controls are matched to. */}
      <Box sx={{ minWidth: '200px' }}>
        <CheckboxMultiSelect
          options={offerOptions}
          value={selectedOffers}
          onChange={handleOffersChange}
          placeholder='All offers'
          multiSelectDisplayText={(count) => `${count} offers selected`}
        />
      </Box>

      <OutlinedInput
        id='outlined-adornment-amount'
        placeholder='Search a member...'
        startAdornment={
          <InputAdornment position='start'>
            <Icons.SearchIcon />
          </InputAdornment>
        }
        onChange={(e) => {
          handleSearch(e.target.value);
        }}
        size='small'
        sx={{
          ...toolbarControlSx,
          // Was `size='small'` (40px) plus an extra `py: 0.2`, which made the
          // search the tallest control in the row.
          // Keeps its current 290px at full width but can now give ground
          // instead of forcing the row to overflow.
          flex: '1 1 290px',
          minWidth: 160,
          maxWidth: 360,
          borderRadius: '8px',
          px: 1.5,
          fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
          fontWeight: 500,
          fontSize: '0.875rem',
          lineHeight: 1.75,
          textTransform: 'none',
          color: '#000',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: '1px solid #EFEEEE',
            borderWidth: '1px',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: '1px solid #EFEEEE',
            borderWidth: '1px',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: '1px solid #EFEEEE',
            borderWidth: '1px',
          },
        }}
      />

      <ProductRequestsDialog
        open={productRequestsHistoryDialog}
        onClose={handleProductRequestsHistoryDialog}
      />

      <ProductRequestsFiltersDialog
        open={productRequestsFiltersDialog}
        onClose={handleProductRequestsFiltersDialog}
        filters={filters}
        onFilterChange={onHandleFilterChange}
      />
    </>
  );
};
